import { Star, ChevronRight, Video } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import UserAvatar from "./UserAvatar";
import TrustBadge, { getTrustTier } from "./TrustBadge";
import type { ItemRatingValue } from "./ItemRating";

interface RelatedReview {
  id: string;
  user: {
    name: string;
    displayName?: string;
    avatar?: string;
    verified?: boolean;
    trustScore?: number;
  };
  restaurant: {
    name: string;
    location: string;
    overallRating: number;
    totalReviews: number;
  };
  dish?: {
    name: string;
    price?: number;
  };
  items?: Array<{
    name: string;
    image: string;
    itemRating: ItemRatingValue;
  }>;
  rating: number;
  itemRating?: ItemRatingValue;
  text: string;
  images?: string[];
  image?: string;
  video?: string;
  createdAt: string;
}

interface RelatedReviewsSheetProps {
  isOpen: boolean;
  onClose: () => void;
  restaurantName: string;
  reviews: RelatedReview[];
  onReviewClick: (reviewId: string) => void;
}

export default function RelatedReviewsSheet({
  isOpen,
  onClose,
  restaurantName,
  reviews,
  onReviewClick
}: RelatedReviewsSheetProps) {
  const getReviewThumbnail = (review: RelatedReview): string | null => {
    if (review.images && review.images.length > 0 && review.images[0]) {
      return review.images[0];
    }
    if (review.items && review.items.length > 0 && review.items[0]?.image) {
      return review.items[0].image;
    }
    if (review.image) {
      return review.image;
    }
    return null;
  };

  const hasVideo = (review: RelatedReview): boolean => {
    if (review.video) return true;
    const media = review.images || [];
    return media.some(m => m?.match(/\.(mp4|webm|mov)$/i));
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="bottom" className="h-auto max-h-[70svh] rounded-t-2xl">
        <SheetHeader className="text-left pb-3">
          <SheetTitle className="text-lg">
            More reviews from {restaurantName}
          </SheetTitle>
        </SheetHeader>

        <ScrollArea className="max-h-[50svh] pr-2">
          <div className="space-y-3 pb-4">
            {reviews.map((review, index) => {
              const thumbnail = getReviewThumbnail(review);
              const isVideo = hasVideo(review);
              const userTrustScore = review.user.trustScore || 50;

              return (
                <button
                  key={review.id}
                  className="w-full bg-muted/50 hover:bg-muted rounded-lg p-3 text-left transition-colors hover-elevate active-elevate-2 border border-border/50"
                  onClick={() => {
                    onReviewClick(review.id);
                    onClose();
                  }}
                  data-testid={`related-review-sheet-${index}`}
                >
                  <div className="flex gap-3">
                    {thumbnail && (
                      <div className="relative w-16 h-16 rounded-md overflow-hidden flex-shrink-0 bg-muted">
                        <img
                          src={thumbnail}
                          alt="Review"
                          className="w-full h-full object-cover"
                        />
                        {isVideo && (
                          <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                            <Video className="w-5 h-5 text-white" />
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <UserAvatar
                          src={review.user.avatar}
                          name={review.user.name}
                          size="sm"
                        />
                        <span className="text-xs font-medium text-foreground truncate">
                          {review.user.displayName || review.user.name}
                        </span>
                        <TrustBadge
                          trustScore={userTrustScore}
                          trustTier={getTrustTier(userTrustScore)}
                          size="xs"
                          showScore={false}
                        />
                      </div>

                      <div className="flex items-center gap-1.5 mb-1">
                        <Star className="w-3.5 h-3.5 fill-rating text-rating flex-shrink-0" />
                        <span className="text-sm font-semibold text-foreground">
                          {review.rating.toFixed(1)}
                        </span>
                        {review.dish?.name && (
                          <span className="text-xs text-muted-foreground truncate">
                            {review.dish.name}
                          </span>
                        )}
                        {review.items && review.items[0] && (
                          <span className="text-xs text-muted-foreground truncate">
                            {review.items[0].name}
                          </span>
                        )}
                      </div>

                      <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                        {review.text}
                      </p>
                    </div>

                    <div className="flex-shrink-0 self-center">
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
